/* eslint-disable function-paren-newline */
import { REMOVE_FROM_CART } from '@constants/amplitudeEvents';
import { CheckoutInfo, CheckoutItem } from '@customTypes/checkout-info';
import Product from '@customTypes/product';
import { ProductDetails } from '@customTypes/product-details';
import { useMemo } from 'react';
import { useEvents } from '@events/EventsProvider';
import { CartItem } from '@customTypes/cart-items';
import HotelDetails from '@customTypes/hotel-details';

export default function useCart(
  checkoutDetails: CheckoutInfo | undefined,
  hotelProducts: ProductDetails | undefined,
) {
  const { track } = useEvents();

  const productsInTheCart = useMemo((): Product[] => {
    if (checkoutDetails && hotelProducts) {
      const uniqueProductIds = Array.from(
        new Set(checkoutDetails.items.map((item) => item.product_id)),
      );

      const nonTimeSlotProductsInTheCart = hotelProducts.products.filter((product: Product) =>
        uniqueProductIds.includes(product.product_id),
      );

      const timeSlotProductsInTheCart = hotelProducts.products.reduce(
        (result: Product[], product: Product) => {
          if (product.is_product_time_group) {
            const matchingIds =
              product.children &&
              product.children.filter((child) => uniqueProductIds.includes(child.product_id));

            if (matchingIds) {
              result.push(...matchingIds);
            }
          }

          return result;
        },
        [],
      );

      return [...timeSlotProductsInTheCart, ...nonTimeSlotProductsInTheCart];
    }

    return [];
  }, [checkoutDetails, hotelProducts]);

  function trackRemoveFromCart(
    removedItem: CheckoutItem | CartItem,
    cartId: string,
    hotel: HotelDetails | undefined,
  ) {
    const removedProduct = productsInTheCart.find((p) => p.product_id === removedItem.product_id);

    track(REMOVE_FROM_CART, {
      cart_id: cartId,
      cart_item_total_price: removedItem.total,
      hotel_id: hotel?.metadata.hotel_id,
      hotel_name: hotel?.name,
      product_addon_ids: removedProduct?.addons
        .filter((addon) => addon.active)
        .map((addon) => addon.id),
      product_addon_names: removedProduct?.addons
        .filter((addon) => addon.active)
        .map((addon) => addon.name),
      product_id: removedProduct?.product_id,
      product_name: removedProduct?.name,
      product_type: removedProduct?.product_type,
    });
  }

  return { productsInTheCart, trackRemoveFromCart };
}
